// src/services/salesOrderFilter.ts
// -------------------------------
// Pure, side-effect-free filtering for the Manage → Unleashed → Sales Orders screen
// (`SalesOrdersAdmin.jsx`). Kept out of the JSX so it's unit-tested independently.
//
// Operates over the grouped `SalesOrder[]` (from src/data/salesOrders) plus the
// per-order push row map (order_number → PushRow, from src/data/salesOrderPushes).
//
// NOTE: the `api_excluded` / `exported` criteria are added in the db-70 batch once
// those columns (and the regenerated PushRow type) exist. This module intentionally
// only filters on columns that exist today, so it stays typecheck-clean with no DB
// dependency.

import type { PushRow } from "../data/salesOrderPushes";
import type { SalesOrder } from "../data/salesOrders";

// Sentinel used in `statuses` to match orders that have no push row yet.
export const NO_PUSH = "none";

export interface SalesOrderFilterCriteria {
  // Free text over order number / customer code / customer reference.
  text?: string;
  // SO type suffixes to include (push.suffix). Empty / undefined = all.
  suffixes?: string[];
  // Push statuses to include (push.status, or NO_PUSH for no push row). Empty = all.
  statuses?: string[];
  // Warehouse codes to include (header.warehouse_code). Empty = all.
  warehouseCodes?: string[];
  // Inclusive order-date range (ISO YYYY-MM-DD compared lexically against order_date).
  dateFrom?: string;
  dateTo?: string;
  // Match a request by ref/id (matches header.request_id or a substring of the order no).
  requestRef?: string;
  // Quick filter: only the ZZZ unrouted safety-net orders.
  zzzOnly?: boolean;
  // Manual-import marks (db-70). "yes"/"no", or undefined = all.
  apiExcluded?: "yes" | "no";
  exported?: "yes" | "no";
}

const lower = (v: unknown): string => (v == null ? "" : String(v)).toLowerCase();

// The order-date as a comparable YYYY-MM-DD string (order_date may be a full ISO
// timestamp; take the date part). Empty string sorts before any real date.
const datePart = (v: string | null): string => (v ? v.slice(0, 10) : "");

/**
 * Filter grouped sales orders by the given criteria. Returns a new array; never
 * mutates its inputs. An order with no matching push row is treated as status
 * `NO_PUSH` and suffix `""`.
 */
export function filterSalesOrders(
  orders: SalesOrder[],
  pushByOrder: Record<string, PushRow>,
  criteria: SalesOrderFilterCriteria
): SalesOrder[] {
  const text = (criteria.text ?? "").trim().toLowerCase();
  const suffixes = criteria.suffixes ?? [];
  const statuses = criteria.statuses ?? [];
  const warehouses = criteria.warehouseCodes ?? [];
  const from = criteria.dateFrom ? criteria.dateFrom.slice(0, 10) : "";
  const to = criteria.dateTo ? criteria.dateTo.slice(0, 10) : "";
  const requestRef = (criteria.requestRef ?? "").trim().toLowerCase();

  return orders.filter((o) => {
    const h = o.header;
    const push = pushByOrder[o.orderNumber];
    const suffix = push?.suffix ?? "";
    const status = push?.status ?? NO_PUSH;

    if (text) {
      const hit =
        lower(o.orderNumber).includes(text) ||
        lower(h.customer_code).includes(text) ||
        lower(h.customer_reference).includes(text);
      if (!hit) return false;
    }

    if (criteria.zzzOnly && suffix !== "ZZZ") return false;

    if (suffixes.length > 0 && !suffixes.includes(suffix)) return false;

    if (statuses.length > 0 && !statuses.includes(status)) return false;

    if (warehouses.length > 0 && !warehouses.includes(h.warehouse_code ?? "")) return false;

    if (from || to) {
      const d = datePart(h.order_date);
      if (!d) return false; // no order date can't satisfy a range
      if (from && d < from) return false;
      if (to && d > to) return false;
    }

    if (requestRef) {
      const hit =
        String(h.request_id ?? "").includes(requestRef) ||
        lower(o.orderNumber).includes(requestRef);
      if (!hit) return false;
    }

    if (criteria.apiExcluded) {
      const excluded = push?.api_excluded ?? false;
      if (excluded !== (criteria.apiExcluded === "yes")) return false;
    }

    if (criteria.exported) {
      const isExported = push?.exported_at != null;
      if (isExported !== (criteria.exported === "yes")) return false;
    }

    return true;
  });
}

// Distinct, sorted suffix values present across the given push rows — powers the
// SO-type multi-select in the UI. Blank suffixes are omitted.
export function distinctSuffixes(pushes: PushRow[]): string[] {
  const set = new Set<string>();
  for (const p of pushes) {
    if (p.suffix) set.add(p.suffix);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

// Distinct, sorted warehouse codes present across the given orders.
export function distinctWarehouses(orders: SalesOrder[]): string[] {
  const set = new Set<string>();
  for (const o of orders) {
    const w = o.header.warehouse_code;
    if (w) set.add(w);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

// ── Grouping by request ──────────────────────────────────────────────────────
// The Sales Orders screen groups a request's suffix-split SOs (2026-00161-NSA,
// -DEV, …) under one expandable parent row keyed on the base ref.

// The base request ref of an order number: strips the trailing -<SUFFIX>[seq]
// (e.g. "2026-00161-NSA" → "2026-00161", "2026-00160-SCH2" → "2026-00160").
export function baseRef(orderNumber: string): string {
  return orderNumber.replace(/-[A-Z]+\d*$/, "");
}

export interface RequestGroup {
  key: string; // stable group key (request_id, or the base ref when id is absent)
  ref: string; // base ref shown on the parent row
  requestId: number | null;
  customerCode: string;
  orders: SalesOrder[];
}

// Group orders by their request (request_id, falling back to the base ref),
// preserving the incoming order of first appearance (so a newest-first list
// yields newest-request-first groups).
export function groupOrdersByRequest(orders: SalesOrder[]): RequestGroup[] {
  const map = new Map<string, RequestGroup>();
  for (const o of orders) {
    const rid = (o.header.request_id ?? null) as number | null;
    const ref = baseRef(o.orderNumber);
    const key = rid != null ? `r${rid}` : `ref:${ref}`;
    let g = map.get(key);
    if (!g) {
      g = { key, ref, requestId: rid, customerCode: o.header.customer_code ?? "", orders: [] };
      map.set(key, g);
    }
    g.orders.push(o);
  }
  // Order each request's sub-SOs by order number, numerically-aware so a base
  // suffix sorts before its numbered variant (…-NSA before …-NSA2, and …-NSA2
  // before …-NSA10) rather than by DB insertion order.
  for (const g of map.values()) {
    g.orders.sort((a, b) =>
      (a.orderNumber || "").localeCompare(b.orderNumber || "", undefined, { numeric: true })
    );
  }
  return [...map.values()];
}

// Roll up a set of push statuses into one label for the parent row: "" for none,
// the shared status when they all agree, otherwise "mixed".
export function rollupStatus(statuses: Array<string | null | undefined>): string {
  const set = new Set<string>();
  for (const s of statuses) if (s) set.add(s);
  if (set.size === 0) return "";
  if (set.size === 1) return [...set][0];
  return "mixed";
}
